import {
    KeyboardEvent,
    MouseEvent as ReactMouseEvent,
    PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    AxleDialogShellDragBounds,
    AxleDialogShellDragHandleProps,
    AxleDialogShellDragOffset,
    AxleDialogShellRect,
} from './AxleDialogShell.types';
import {
    DRAG_ORIGIN,
    clampDragOffset,
    getLayoutRect,
    isDragHandleTarget,
    toTranslateStyle,
} from './AxleDialogShell.utils';

// Marks the element the drag offset moves, so the handle can find it without
// depending on MUI's own class names.
export const DIALOG_SHELL_PAPER_ATTRIBUTE = 'data-axle-dialog-shell-paper';

const DEFAULT_MIN_VISIBLE = 72;
const KEYBOARD_STEP = 16;

const KEYBOARD_STEPS: Partial<Record<string, AxleDialogShellDragOffset>> = {
    ArrowLeft: { x: -KEYBOARD_STEP, y: 0 },
    ArrowRight: { x: KEYBOARD_STEP, y: 0 },
    ArrowUp: { x: 0, y: -KEYBOARD_STEP },
    ArrowDown: { x: 0, y: KEYBOARD_STEP },
};

type DragSession = {
    pointerId: number;
    startX: number;
    startY: number;
    startOffset: AxleDialogShellDragOffset;
    paperRect: AxleDialogShellRect;
};

const getViewport = () => ({ width: window.innerWidth, height: window.innerHeight });

const findPaper = (element: HTMLElement): HTMLElement | null =>
    element.closest<HTMLElement>(`[${DIALOG_SHELL_PAPER_ATTRIBUTE}]`);

type UseDialogDragArgs = {
    enabled: boolean;
    bounds?: AxleDialogShellDragBounds;
    minVisible?: number;
    resetOnDoubleClick?: boolean;
};

export const useDialogDrag = ({
    enabled,
    bounds = 'contain',
    minVisible = DEFAULT_MIN_VISIBLE,
    resetOnDoubleClick = false,
}: UseDialogDragArgs) => {
    const [offset, setOffset] = useState<AxleDialogShellDragOffset>(DRAG_ORIGIN);
    const [isDragging, setIsDragging] = useState(false);
    const offsetRef = useRef(offset);
    const sessionRef = useRef<DragSession | null>(null);
    const paperRef = useRef<HTMLElement | null>(null);

    const applyOffset = useCallback(
        (next: AxleDialogShellDragOffset, paperRect: AxleDialogShellRect) => {
            const clamped = clampDragOffset({
                offset: next,
                paperRect,
                viewport: getViewport(),
                bounds,
                minVisible,
            });
            offsetRef.current = clamped;
            setOffset(clamped);
        },
        [bounds, minVisible],
    );

    const resetPosition = useCallback(() => {
        offsetRef.current = DRAG_ORIGIN;
        setOffset(DRAG_ORIGIN);
    }, []);

    useEffect(() => {
        if (!enabled) {
            resetPosition();
        }
    }, [enabled, resetPosition]);

    // A viewport that shrinks under a moved dialog can push it out of reach.
    useEffect(() => {
        const onResize = () => {
            const paper = paperRef.current;
            const current = offsetRef.current;
            if (paper === null || (current.x === 0 && current.y === 0)) {
                return;
            }
            applyOffset(current, getLayoutRect(paper));
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [applyOffset]);

    /* Tracking on the window rather than the handle keeps a fast drag that outruns
     * the cursor, and a release outside the window, from stranding the session.
     */
    useEffect(() => {
        if (!isDragging) {
            return;
        }
        const onPointerMove = (event: PointerEvent) => {
            const session = sessionRef.current;
            if (session === null || session.pointerId !== event.pointerId) {
                return;
            }
            applyOffset(
                {
                    x: session.startOffset.x + (event.clientX - session.startX),
                    y: session.startOffset.y + (event.clientY - session.startY),
                },
                session.paperRect,
            );
        };
        const onPointerUp = (event: PointerEvent) => {
            const session = sessionRef.current;
            if (session === null || session.pointerId !== event.pointerId) {
                return;
            }
            sessionRef.current = null;
            setIsDragging(false);
        };
        const { userSelect } = document.body.style;
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
        return () => {
            document.body.style.userSelect = userSelect;
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
        };
    }, [applyOffset, isDragging]);

    const onPointerDown = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            if (!enabled || event.button !== 0 || !isDragHandleTarget(event.target)) {
                return;
            }
            const paper = findPaper(event.currentTarget);
            if (paper === null) {
                return;
            }
            paperRef.current = paper;
            const { clientX, clientY, pointerId } = event;
            setIsDragging(true);
            sessionRef.current = {
                pointerId,
                startX: clientX,
                startY: clientY,
                startOffset: offsetRef.current,
                paperRect: getLayoutRect(paper),
            };
        },
        [enabled],
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>) => {
            const step = KEYBOARD_STEPS[event.key];
            if (!enabled || step === undefined || !isDragHandleTarget(event.target)) {
                return;
            }
            const paper = findPaper(event.currentTarget);
            if (paper === null) {
                return;
            }
            event.preventDefault();
            paperRef.current = paper;
            const current = offsetRef.current;
            applyOffset({ x: current.x + step.x, y: current.y + step.y }, getLayoutRect(paper));
        },
        [applyOffset, enabled],
    );

    const onDoubleClick = useCallback(
        (event: ReactMouseEvent<HTMLElement>) => {
            if (!isDragHandleTarget(event.target)) {
                return;
            }
            resetPosition();
        },
        [resetPosition],
    );

    const handleProps = useMemo<AxleDialogShellDragHandleProps>(
        () => ({
            onPointerDown,
            onKeyDown,
            style: {
                cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : undefined,
                touchAction: enabled ? 'none' : undefined,
            },
            ...(enabled && { tabIndex: 0, 'aria-description': 'Drag to reposition dialog' }),
            ...(enabled && resetOnDoubleClick && { onDoubleClick }),
        }),
        [enabled, isDragging, onDoubleClick, onKeyDown, onPointerDown, resetOnDoubleClick],
    );

    const paperTranslate = useMemo(() => toTranslateStyle(offset), [offset]);

    return {
        handleProps,
        paperTranslate,
        resetPosition,
    };
};
